// Customer-friendly nutrition-gap wording.
//
// The trusted generator returns per-day `target_gaps` where each
// nutrient is a signed number using the contract:
//     target_gap = target - actual
// So:
//   POSITIVE value → customer is BELOW target
//   NEGATIVE value → customer is ABOVE target
//   ZERO            → on target
//
// Customers find "+14" and "-10 g" confusing, so we convert to a plain
// sentence and always show the magnitude (absolute value):
//   +14 (calories)  -> "14 calories below target"
//   -12 (calories)  -> "12 calories above target"
//   +1.1 (protein)  -> "1.1 g protein below target"
//   -62.3 (protein) -> "62.3 g protein above target"
//   +70.2 (carbs)   -> "70.2 g carbs below target"
//   -0.9 (fat)      -> "0.9 g fat above target"
//    0              -> "on target"
// The underlying returned value is never modified.

const NUTRIENT_LABELS = {
  calories: { name: 'calories', kind: 'int' },
  protein: { name: 'g protein', kind: 'dec1' },
  carbohydrates: { name: 'g carbs', kind: 'dec1' },
  fat: { name: 'g fat', kind: 'dec1' },
};

function fmtInt(n) {
  return Math.abs(Math.round(n)).toString();
}
function fmtDec1(n) {
  const v = Math.abs(Math.round(n * 10) / 10);
  return v.toString();
}

// Returns { text, direction: 'below'|'above'|'ontarget', magnitude }.
// direction reflects the customer-facing reality:
//   'below'  = actual is below the target (positive gap value)
//   'above'  = actual is above the target (negative gap value)
export function describeGap(nutrient, value) {
  const meta = NUTRIENT_LABELS[nutrient];
  if (!meta) return { text: '', direction: 'ontarget', magnitude: 0 };
  if (!Number.isFinite(value)) return { text: '', direction: 'ontarget', magnitude: 0 };
  const rounded = meta.kind === 'int'
    ? Math.round(value)
    : Math.round(value * 10) / 10;
  if (rounded === 0) {
    return { text: 'on target', direction: 'ontarget', magnitude: 0 };
  }
  const magnitudeStr = meta.kind === 'int' ? fmtInt(rounded) : fmtDec1(rounded);
  // Positive gap = actual < target = "below". Negative gap = "above".
  const dir = rounded > 0 ? 'below' : 'above';
  const label = nutrient === 'calories'
    ? `${magnitudeStr} calories ${dir} target`
    : `${magnitudeStr} ${meta.name} ${dir} target`;
  return { text: label, direction: dir, magnitude: Math.abs(rounded) };
}

export function gapColorFor(direction) {
  if (direction === 'ontarget') return 'var(--success, #2e7d32)';
  if (direction === 'below') return 'var(--warning, #b26a00)';
  return 'var(--warning, #b26a00)';
}
